import React, { useState, useEffect } from 'react';
import { getAllFlags, enableFeatureFlag, disableFeatureFlag, FLAGS } from '../lib/featureFlags';

/**
 * Feature toggle component for admins to control feature rollout
 * This component should only be visible to admins
 */
const FeatureToggle: React.FC = () => {
  const [flags, setFlags] = useState<Record<string, boolean>>({});
  const [isOpen, setIsOpen] = useState(false);

  // Load flags on component mount
  useEffect(() => {
    const loadFlags = () => {
      setFlags(getAllFlags());
    };
    
    loadFlags();
    // Set up periodic refresh
    const interval = setInterval(loadFlags, 10000);
    
    return () => clearInterval(interval);
  }, []);

  const toggleFlag = (flagName: string) => {
    if (flags[flagName]) {
      disableFeatureFlag(flagName);
    } else {
      enableFeatureFlag(flagName);
    }
    
    // Update local state
    setFlags({
      ...flags,
      [flagName]: !flags[flagName]
    });
  };

  const togglePanel = () => {
    setIsOpen(!isOpen);
  };

  // Map of flag names to friendly display names
  const flagLabels: Record<string, string> = {
    [FLAGS.MOBILE_UI_IMPROVEMENTS]: 'Mobile UI Improvements (Master)',
    [FLAGS.RESPONSIVE_MESSAGING]: 'Responsive Messaging',
    [FLAGS.IMPROVED_HAMBURGER]: 'Improved Hamburger Menu',
  };

  return (
    <div className="fixed bottom-4 right-4 z-50">
      <button
        onClick={togglePanel}
        className="bg-gray-800 text-white p-3 rounded-full shadow-lg hover:bg-gray-700"
        title="Feature Flags"
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
        </svg>
      </button>
      
      {isOpen && (
        <div className="absolute bottom-14 right-0 bg-white p-4 rounded-lg shadow-xl border border-gray-200 w-80">
          <h3 className="font-medium text-gray-900 mb-3">Feature Flags</h3>
          <div className="space-y-3">
            {Object.keys(flagLabels).map((flagName) => (
              <div key={flagName} className="flex items-center justify-between">
                <span className="text-sm">{flagLabels[flagName]}</span>
                <button
                  onClick={() => toggleFlag(flagName)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-rose-500 focus:ring-offset-2 ${
                    flags[flagName] ? 'bg-rose-600' : 'bg-gray-200'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      flags[flagName] ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            ))}
          </div>
          <div className="mt-4 text-xs text-gray-500">
            Changes take effect immediately. Reload the page to see all updates.
          </div>
        </div>
      )}
    </div>
  );
};

export default FeatureToggle;
